import Layout from 'antd/lib/layout';
import React, { useState } from 'react';
import { FilterToggleButton } from '../../../components/sidebar-filters/FilterToggleButton';
import { ICreateSavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { SidebarFiltersGroupCollapse } from '../../../components/sidebar-filters/SidebarFiltersGroupCollapse';
import { ISite } from '../../../domain/site/interface';
import { useLocalStorage } from '../../../domain/useLocalStorage';
import { EnergyUsageFiltersPanel } from './components/EnergyUsageFiltersPanel';
import { EnergyUsageSidebarWrapper } from './components/EnergyUsageSidebarWrapper';
import { SaveSiteFiltersModal } from './components/SaveSiteFiltersModal';
import { SitesPanel } from './components/SitesPanel';
import { ENERGY_USAGE_PAGE_CONST } from './constants';
import { useEnergyUsageSidebarFiltersContext } from './context/EnergyUsageSidebarFiltersContext';

interface IEnergyUsageDataSidebarProps {
    onLoadData: (siteIds: number[], sites: ISite[]) => void;
    onCollapseChange: (value: boolean) => void;
    isChartsDataLoading: boolean;
}

export const EnergyUsageSidebar: React.FC<IEnergyUsageDataSidebarProps> = ({ onLoadData, isChartsDataLoading, onCollapseChange }) => {
    const {
        selectedSavedFilter,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        isCreateFilterLoading,
        handleUpdateSavedFilter,
        isUpdateFilterLoading,
        selectedSiteSans,
    } = useEnergyUsageSidebarFiltersContext();

    const [collapsed, setCollapsed] = useLocalStorage(ENERGY_USAGE_PAGE_CONST.localStorage.sidebarCollapsedKey, false);
    const [siteFiltersCollapsed, setSiteFiltersCollapsed] = useLocalStorage(ENERGY_USAGE_PAGE_CONST.localStorage.siteFiltersCollapsedKey, false);

    const [saveFiltersModalOpen, setSaveFiltersModalOpen] = useState(false);

    const handleCollapseChange = (value: boolean) => {
        setCollapsed(value);

        onCollapseChange(value);
    };

    const handleOpenSaveFiltersModal = (createSavedFilter: ICreateSavedFilter) => {
        setSaveFiltersModalOpen(true);

        handleSavedFilterSelect(createSavedFilter);
    };

    const handleCancelSaveFilters = () => {
        setSaveFiltersModalOpen(false);
        handleSavedFilterSelect(null);
    };

    const handleSaveFilters = async (filterData: ICreateSavedFilter) => {
        const upsertSavedFilter = filterData.id ? handleUpdateSavedFilter : handleCreateSavedFilter;

        await upsertSavedFilter(filterData);

        setSaveFiltersModalOpen(false);
        handleSavedFilterSelect(null);
    };

    return (
        <>
            <EnergyUsageSidebarWrapper
                collapsed={collapsed}
                onCollapseChange={handleCollapseChange}
                siteFiltersCollapsed={siteFiltersCollapsed}
                setSiteFiltersCollapsed={setSiteFiltersCollapsed}
            >
                <Layout.Content
                    style={{
                        padding: 0,
                        margin: 0,
                        height: '100%',
                    }}
                >
                    <SidebarFiltersGroupCollapse
                        collapsed={collapsed}
                        setCollapsed={handleCollapseChange}
                        selectedItemsCount={selectedSiteSans.length}
                    />

                    {!collapsed && (
                        <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
                            <div
                                style={{
                                    maxWidth: `${siteFiltersCollapsed ? ENERGY_USAGE_PAGE_CONST.filtersCollapsedWidth : ENERGY_USAGE_PAGE_CONST.filtersWidth}px`,
                                    width: '100%',
                                }}
                            >
                                <FilterToggleButton siteFiltersCollapsed={siteFiltersCollapsed} setSiteFiltersCollapsed={setSiteFiltersCollapsed} />
                                <EnergyUsageFiltersPanel
                                    siteFiltersCollapsed={siteFiltersCollapsed}
                                    onOpenSaveFiltersModal={handleOpenSaveFiltersModal}
                                />
                            </div>

                            <div style={{ maxWidth: `${ENERGY_USAGE_PAGE_CONST.sitesPanelWidth}px`, width: '100%' }}>
                                <SitesPanel
                                    collapsed={collapsed}
                                    isChartsDataLoading={isChartsDataLoading}
                                    onLoadData={onLoadData}
                                    onCollapseChange={handleCollapseChange}
                                />
                            </div>
                        </div>
                    )}
                </Layout.Content>
            </EnergyUsageSidebarWrapper>

            {saveFiltersModalOpen && selectedSavedFilter && (
                <SaveSiteFiltersModal
                    isLoading={isCreateFilterLoading || isUpdateFilterLoading}
                    initialSavedFilter={selectedSavedFilter}
                    onCancel={handleCancelSaveFilters}
                    onSave={handleSaveFilters}
                />
            )}
        </>
    );
};
